/* Per-language track selection.

   This is the one feature `delivery-architecture.md` recommended Material for
   MkDocs *for*: "A student clicks 'JavaScript' once, in week one, and every code
   sample on every page of the site is JavaScript from then on."

   Three properties matter, and all three are here:
     1. every track block on the page switches together
     2. the choice survives a reload
     3. it survives navigation, because it is not per-page state

   It also syncs across browser tabs, which MkDocs does not do.

   Note this is deliberately NOT in the URL. The selection is a durable student
   preference, not a property of the page — a shared link should open on the
   reader's own track, not the sender's.
*/

import { createContext, useContext, useState, useEffect, useCallback } from 'react';

import { LANGUAGES } from '../config.js';

/* Every track the content model knows about. What a student may actually pick
   is this list filtered by config.LANGUAGES — see TRACKS below. */
const ALL_TRACKS = [
  { id: 'js',     label: 'JavaScript' },
  { id: 'python', label: 'Python' },
  { id: 'ruby',   label: 'Ruby' },
];

/** The tracks on offer today. */
export const TRACKS = ALL_TRACKS.filter(t => LANGUAGES.includes(t.id));

const KEY = 'js1.track';
const DEFAULT = 'js';

const TrackContext = createContext(null);

/* A stored choice that is no longer offered must not survive. Someone who
   picked Python before it was withdrawn would otherwise land on a page whose
   track blocks do not render. */
const allowed = id => TRACKS.some(t => t.id === id);

const read = () => {
  try {
    const saved = localStorage.getItem(KEY);
    return saved && allowed(saved) ? saved : DEFAULT;
  } catch { return DEFAULT; }
};

export function TrackProvider({ children }) {
  const [track, setTrackState] = useState(read);

  const setTrack = useCallback(id => {
    setTrackState(id);
    try { localStorage.setItem(KEY, id); } catch { /* private mode — session only */ }
  }, []);

  /* Another tab changed the choice. */
  useEffect(() => {
    const on = e => { if (e.key === KEY && e.newValue && allowed(e.newValue)) setTrackState(e.newValue); };
    window.addEventListener('storage', on);
    return () => window.removeEventListener('storage', on);
  }, []);

  return <TrackContext.Provider value={[track, setTrack]}>{children}</TrackContext.Provider>;
}

export function useTrack() {
  const ctx = useContext(TrackContext);
  if (!ctx) throw new Error('useTrack must be used inside <TrackProvider>');
  return ctx;
}
